import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  ArrowLeft, FileText, CheckCircle, AlertCircle, Users, Calendar,
  ChevronRight, Trash2, Eye, Upload, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { marketingAPI } from '../../utils/api';

export default function MarketingImportHistory() {
  const navigate = useNavigate();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchImports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await marketingAPI.getImports();
      setImports(data.imports || []);
    } catch {
      toast.error('Failed to load import history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await marketingAPI.deleteImport(deleteTarget.id);
      toast.success('Import batch deleted');
      setDeleteTarget(null);
      fetchImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Import History</h1>
            <p className="text-sm text-slate-500">All CSV lead import batches</p>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchImports} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/marketing/upload')}
              className="bg-teal-600 hover:bg-teal-700 gap-1.5">
              <Upload className="w-4 h-4" /> New Import
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-5 h-20" />
              </Card>
            ))}
          </div>
        ) : imports.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium text-slate-600">No imports yet</p>
            <p className="text-sm mt-1">Upload your first CSV to get started</p>
            <Button className="mt-4 bg-teal-600 hover:bg-teal-700"
              onClick={() => navigate('/admin/marketing/upload')}>
              Import Leads
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {imports.map((imp) => (
              <Card key={imp.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-slate-800 truncate">{imp.file_name}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(imp.import_date).toLocaleString()}
                            </span>
                            {imp.assigned_to_name && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {imp.assigned_to_name}
                              </span>
                            )}
                            <span>by {imp.assigned_by_name || '—'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8 text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                            onClick={() => setDeleteTarget(imp)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-3">
                        <div className="flex items-center gap-1.5 text-sm">
                          <span className="font-bold text-emerald-600">{imp.imported_count}</span>
                          <span className="text-slate-500 text-xs">imported</span>
                        </div>
                        {imp.duplicate_count > 0 && (
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="font-bold text-amber-600">{imp.duplicate_count}</span>
                            <span className="text-slate-500 text-xs">duplicates</span>
                          </div>
                        )}
                        {imp.failed_rows?.length > 0 && (
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="font-bold text-rose-600">{imp.failed_rows.length}</span>
                            <span className="text-slate-500 text-xs">failed</span>
                          </div>
                        )}
                        <Badge className="bg-emerald-100 text-emerald-700 ml-auto">
                          <CheckCircle className="w-3 h-3 mr-1" /> {imp.status}
                        </Badge>
                      </div>

                      {/* Failed rows preview */}
                      {imp.failed_rows?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {imp.failed_rows.slice(0, 2).map((r, i) => (
                            <p key={i} className="text-xs text-rose-500 bg-rose-50 px-2 py-1 rounded">{r}</p>
                          ))}
                          {imp.failed_rows.length > 2 && (
                            <p className="text-xs text-slate-400">+{imp.failed_rows.length - 2} more errors</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Import Batch?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the import batch record and remove any unworked leads (stage: new_lead) from this batch.
              Leads that have been moved to other stages will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
